import { Link } from '@mui/material';
import { Paths } from 'services/navigation/paths';

const onboardingElementIds = {
  homePageContainer: 'home-page-container',

  campaignsButton: 'campaigns-button',
  createNewCampaignButton: 'create-campaign-button',
  startOutboundCampaignButton: 'start-outbound-campaign-button',
  createNewCampaignWebChatChannelButton:
    'create-new-campaign-web-chat-channel-button',
  createNewCampaignNewCandidateOutreachButton:
    'create-new-campaign-new-candidate-outreach-button',
  createNewCampaignJobDescriptionField: 'create-new-campaign-job-description',
  createNewCampaignSummaryHeader: 'create-new-campaign-summary-header',
  createNewCampaignScreeningQuestionsHeader:
    'create-new-campaign-screening-questions-header',

  createNewCampaignUploadFile: 'create-new-campaign-upload-file',
  createNewCampaignAgentName: 'create-new-campaign-agent-name',
  createNewCampaignSelectWebChatOpeningMessage:
    'select-web-chat-opening-message',
  createNewCampaignSelectWebChatWelcomeMessage:
    'select-web-chat-welcome-message',
  createNewCampaignSelectWebChatTemplates: 'select-web-chat-templates',

  createNewCampaignSelectClosingMethod: 'select-closing-method',
  createNewCampaignCustomizeAutoResponse: 'customize-auto-response',
  createNewCampaignSimulateConversation: 'simulate-conversation',

  launchCampaignReadyToSendButton: 'launch-campaign-ready-to-send-button',
  createCampaignPageDashboard: 'campaign-page-dashboard',

  sendCampaignButton: 'send-campaign-button',
  campaignViewConversation: 'campaign-view-conversation',

  campaignOpeningMessagesContainer: 'campaign-opening-messages-container',
  campaignViewConversationDoneButton: 'campaign-view-conversation-done-button'
};

export type OnboardingStep = {
  content: string | JSX.Element;
  title: string;
  placement?: 'top' | 'bottom' | 'center' | 'left' | 'right';
  targetId: string;
  hideNextButton?: boolean;
  buttonAction?: 'pause' | 'next';
  buttonText?: string;
  showOnlyWhenCurrentTarget?: boolean;
  useControlledContent?: boolean;
};

const onboardingSteps: OnboardingStep[] = [
  {
    title: 'Welcome to Popp',
    content:
      "Let's create your first AI agent and start automating candidate conversations.",
    placement: 'center',
    targetId: 'body'
  },
  {
    targetId: onboardingElementIds.campaignsButton,
    title: 'Go to Campaigns',
    content: 'To create an AI agent, we need to create a new campaign.'
  },
  {
    targetId: onboardingElementIds.createNewCampaignButton,
    title: 'Create new campaign',
    content: 'Click here to create a new campaign'
  },
  {
    targetId: onboardingElementIds.startOutboundCampaignButton,
    title: "Let's create an outbound campaign",
    content: (
      <span>
        We're going to be initiating conversations with candidates, so this
        campaign will be <strong>outbound</strong>.
      </span>
    )
  },
  {
    targetId: onboardingElementIds.createNewCampaignWebChatChannelButton,
    title: 'Select Web Chat as the channel',
    content:
      'This is how your AI agent will communicate with candidates. By selecting Web Chat, candidates will be sent an SMS with a link to an online chat. Popp also supports WhatsApp, but this is only available on paid plans.'
  },
  {
    targetId: onboardingElementIds.createNewCampaignNewCandidateOutreachButton,
    title: "Let's reach out to new candidates",
    content:
      "For this test campaign, let's assume you are reaching out to candidates cold. You can also reach out to candidates who have already applied to a role. In each case, the start of the conversation will be slightly different."
  },
  {
    targetId: onboardingElementIds.createNewCampaignJobDescriptionField,
    title: 'Add role information',

    content: (
      <span>
        You can copy and paste a job description here. The more detail, the
        better. Feel free to use your own job description, or use{' '}
        <Link
          href="https://docs.google.com/document/d/19wnWFts9Z_jkK-lDthIFVTpSjIEgfoU2xCzQh2ISJUM/edit"
          target="_blank"
          rel="noopener noreferrer"
        >
          this example
        </Link>
        .
      </span>
    ),
    hideNextButton: true,
    placement: 'top'
  },
  {
    targetId: onboardingElementIds.createNewCampaignSummaryHeader,
    title: 'Review your job description details',
    content:
      "Popp's AI has extracted the relevant information from the job description. This will be used to power candidates conversations. Review the extracted information, and feel free to add, edit or remove as you wish."
  },

  {
    targetId: onboardingElementIds.createNewCampaignScreeningQuestionsHeader,
    title: 'Review the screening questions',
    content:
      "Popp's AI has generated suggested screening questions from the job description. Candidates will be asked these questions if they demonstrate interest. Review the screening questions, and feel free to add, edit or remove as you wish.",
    hideNextButton: true
  },
  {
    targetId: onboardingElementIds.createNewCampaignUploadFile,
    title: 'Upload a CSV file with your contact details',
    useControlledContent: true,
    content: '',
    hideNextButton: true
  },
  {
    targetId: onboardingElementIds.createNewCampaignAgentName,
    title: 'Personalise your agent',
    content: "Choose your agent's name and conversational tone.",
    placement: 'top',
    hideNextButton: true
  },
  {
    targetId: onboardingElementIds.createNewCampaignSelectWebChatTemplates,
    title: 'Choose your opening messages',
    content:
      'Step 1: Select the very first message candidates will receive by SMS. By default, it will contain a link to an online chat with your AI agent. Step 2: Select the welcome message that will be displayed at the top of the online chat when the candidate enters it.',
    placement: 'top',
    hideNextButton: true
  },
  {
    targetId: onboardingElementIds.createNewCampaignSelectClosingMethod,
    title: 'Configure how the conversation will end for successful candidates',
    content:
      'ou can either provide an online calendar link, inform the candidate that they can expect a call back, or specify a custom success message.',
    placement: 'top',
    hideNextButton: true
  },
  {
    targetId: onboardingElementIds.createNewCampaignCustomizeAutoResponse,
    title: 'Customise responses in different scenarios',
    content:
      'Select what message to send if (a) the candidate is not interested, and (b) once the conversation has ended and the AI agent has stopped responding.',
    placement: 'top',
    hideNextButton: true
  },
  {
    targetId: onboardingElementIds.createNewCampaignSimulateConversation,
    title: "Now, let's simulate the conversation!",
    content:
      "Here you can simulate the conversation as though you're the candidate. Once happy, click Generate messages in the bottom right corner.",
    placement: 'top',
    hideNextButton: true
  },
  {
    targetId: onboardingElementIds.campaignOpeningMessagesContainer,
    title: 'Generating messages',
    content:
      'The messages are being generated. You can close and wait for the messages to be generated.',
    placement: 'center',
    buttonText: 'Close',
    showOnlyWhenCurrentTarget: true
  },
  {
    targetId: onboardingElementIds.launchCampaignReadyToSendButton,
    title: 'Your campaign is now ready to launch',
    content: 'To initiate launch, click Launch campaign.',
    placement: 'top',
    hideNextButton: true
  },

  {
    targetId: onboardingElementIds.sendCampaignButton,
    content:
      'Here, you can choose to launch your campaign now or schedule for a later date. Click Send now to launch your campaign immediately.',
    placement: 'bottom',
    title: 'Send your campaign now',
    hideNextButton: true
  },

  {
    targetId: onboardingElementIds.createCampaignPageDashboard,
    title: 'Check your mobile device!',
    content: (
      <span>
        Your campaign is being sent, you should receive an SMS in a moment. If
        you haven't,{' '}
        <Link
          href={`.${Paths.CONTACT_SUPPORT}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          contact support
        </Link>
      </span>
    ),
    placement: 'center',
    showOnlyWhenCurrentTarget: true
  },
  {
    targetId: onboardingElementIds.createCampaignPageDashboard,
    title: 'Welcome to your dashboard',
    content:
      'This is where you can manage every conversation your AI agent is having in this campaign. View high level metrics at the top, and jump in and out of conversations as you please. You have complete control.',
    placement: 'center'
  },

  {
    targetId: onboardingElementIds.campaignViewConversation,
    title: 'View conversation',
    content:
      'By clicking here, you can view and manage any conversation your AI agent is having with a candidate.',
    hideNextButton: true
  },

  // Last step
  {
    targetId: onboardingElementIds.campaignViewConversationDoneButton,
    title: "That's the end of the tour!",
    content: (
      <span>
        Well done, you've reached the end of the tour! But fear not - that was
        just the tip of the iceberg. Popp uses AI to dramatically increase
        productivity when performing various recruitment tasks - why not explore
        the rest of the product and see for yourself! If you need a hand, or
        just want to provide some feedback, drop us a note{' '}
        <Link
          href={`.${Paths.CONTACT_SUPPORT}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          here
        </Link>
      </span>
    ),

    buttonText: 'Finish tour'
  }
];

const disableOverlayForTargetIds = [
  onboardingElementIds.createNewCampaignJobDescriptionField,
  onboardingElementIds.createNewCampaignSummaryHeader,
  onboardingElementIds.createNewCampaignScreeningQuestionsHeader,
  onboardingElementIds.createNewCampaignUploadFile,
  onboardingElementIds.createNewCampaignAgentName,
  onboardingElementIds.createNewCampaignSelectWebChatOpeningMessage,
  onboardingElementIds.createNewCampaignSelectWebChatWelcomeMessage,
  onboardingElementIds.createNewCampaignSelectClosingMethod,
  onboardingElementIds.createNewCampaignCustomizeAutoResponse,
  onboardingElementIds.createNewCampaignSimulateConversation,
  onboardingElementIds.createNewCampaignSelectWebChatTemplates,
  onboardingElementIds.launchCampaignReadyToSendButton,
  onboardingElementIds.campaignViewConversation,
  onboardingElementIds.campaignViewConversationDoneButton
];

const globalTargetIds = [onboardingElementIds.campaignsButton];

export {
  onboardingElementIds,
  onboardingSteps,
  disableOverlayForTargetIds,
  globalTargetIds
};
